import {computed, ref, watch, onMounted} from "vue"
import {defineStore} from "pinia"
import {useResizeObserver, useWindowSize} from "@vueuse/core"

type PageWidth = {
  mobile: boolean
  desktop: boolean
}
interface Page {
  width: PageWidth
}

export const usePageStore = defineStore("page", () => {
  // state
  const page = ref({
    width: {
      mobile: true,
      desktop: false,
    },
  } as Page)
  const sidebarVisible = ref(false)
  const mobileHeader = ref<HTMLElement | null>(null)
  const headerHeight = ref(0)

  const {height: windowHeight} = useWindowSize()
  // Watch for changes in window height and update the CSS variable
  watch(windowHeight, (newHeight) => {
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty("--app-height", `${newHeight}px`)
    }
  })

  // getters
  const isMobile = computed(() => page.value.width.mobile)
  const isDesktop = computed(() => page.value.width.desktop)

  // actions
  function initWindowSize(innerWidth: number) {
    const pageKeys = Object.keys(page.value.width)
    pageKeys?.forEach((k) => (page.value.width[k as keyof typeof page.value.width] = false))
    if (innerWidth < 1024) {
      page.value.width.mobile = true
    } else {
      page.value.width.desktop = true
    }
  }

  const updateHeaderHeight = () => {
    if (mobileHeader.value) {
      headerHeight.value = mobileHeader.value.offsetHeight || 0
    } else {
      headerHeight.value = 0
    }
  }

  const updateCSSVariables = () => {
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty(
        "--mobile-header-height",
        `${headerHeight.value}px`
      )
    }
  }

  const setMobileHeaderHeight = () => {
    updateHeaderHeight()
    updateCSSVariables()
  }

  const initializeResizeObserver = () => {
    useResizeObserver(mobileHeader, () => {
      setMobileHeaderHeight()
    })
  }

  const watchRestaurantTitle = (restaurantTitle: () => string) => {
    watch(restaurantTitle, () => {
      setMobileHeaderHeight()
    })
  }

  onMounted(() => {
    setMobileHeaderHeight()
  })

  return {
    page,
    isMobile,
    isDesktop,
    sidebarVisible,
    initWindowSize,
    mobileHeader,
    headerHeight,
    windowHeight,
    setMobileHeaderHeight,
    initializeResizeObserver,
    watchRestaurantTitle,
  }
})
